import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sun } from "lucide-react";
import { IMG, waLink, scrollToSection } from "../data/site";
import { useLang } from "../i18n/LanguageContext";
import { WhatsAppIcon, EASE } from "./Reveal";

export default function Hero({ introDone }) {
  const { T } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.75]);

  const start = introDone ? "visible" : "hidden";

  return (
    <section
      ref={ref}
      id="inicio"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-end overflow-hidden"
    >
      {/* Parallax photograph */}
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <motion.img
          src={IMG.hero}
          alt="Hotel Federasol · D'Angelo 475, Federación, Entre Ríos"
          className="h-full w-full object-cover"
          initial={{ scale: 1.14, opacity: 0.6 }}
          animate={introDone ? { scale: 1.06, opacity: 1 } : {}}
          transition={{ duration: 2.2, ease: EASE }}
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-[#2b1e1c]/55 via-[#2b1e1c]/15 to-[#2b1e1c]/10"
      />

      {/* Content panel */}
      <div className="container-x relative z-10 pb-12 pt-28 md:pb-24 md:pt-40">
        <motion.div
          data-testid="hero-panel"
          className="max-w-xl rounded-[2rem] border border-federasol-rose/25 bg-federasol-cream/90 p-6 shadow-[0_30px_80px_-30px_rgba(43,30,28,0.5)] backdrop-blur-md sm:p-10 md:p-12"
          initial={{ opacity: 0, y: 34 }}
          animate={introDone ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
        >
          <p data-testid="hero-eyebrow" className="eyebrow flex items-center gap-2">
            <Sun className="h-4 w-4" />
            {T.hero.eyebrow}
          </p>

          <h1 data-testid="hero-headline" className="mt-4 font-serif text-4xl font-medium leading-[1.05] text-federasol-cocoa sm:mt-5 sm:text-6xl">
            {T.hero.lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1.5 -mb-1.5">
                <motion.span
                  className={`block ${i === 1 ? "italic text-federasol-terra" : ""}`}
                  initial={{ y: "112%" }}
                  animate={introDone ? { y: 0 } : {}}
                  transition={{ duration: 1.1, delay: 0.7 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="mt-4 text-sm leading-relaxed text-federasol-muted sm:mt-6 sm:text-base"
            initial={{ opacity: 0, y: 16 }}
            animate={introDone ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          >
            {T.hero.desc}
          </motion.p>

          <motion.div
            className="mt-6 flex flex-col gap-3.5 sm:mt-8 sm:flex-row sm:items-center"
            initial={{ opacity: 0, y: 16 }}
            animate={introDone ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          >
            <a
              href={waLink(T.wa.default)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="btn-primary"
            >
              <WhatsAppIcon className="h-5 w-5" />
              {T.hero.ctaWhats}
            </a>
            <a
              href="#habitaciones"
              data-testid="hero-rooms-cta"
              onClick={(e) => scrollToSection(e, "#habitaciones")}
              className="btn-outline"
            >
              {T.hero.ctaRooms}
            </a>
          </motion.div>

          <motion.p
            className="mt-5 border-t border-federasol-terra/15 pt-4 text-xs font-medium uppercase tracking-[0.18em] text-federasol-muted sm:mt-7 sm:pt-5"
            initial={{ opacity: 0 }}
            animate={introDone ? { opacity: 1 } : {}}
            transition={{ duration: 1, delay: 1.5 }}
          >
            {T.hero.trust}
          </motion.p>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 text-federasol-cream lg:flex"
        initial={{ opacity: 0 }}
        animate={introDone ? { opacity: 1 } : {}}
        transition={{ delay: 1.8, duration: 1 }}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em] [writing-mode:vertical-rl]">
          {T.hero.scroll}
        </span>
        <motion.span
          className="block h-14 w-px origin-top bg-federasol-cream/70"
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}
